import React, { useEffect, useState } from "react"

import { cloneDeep } from "lodash"
import { CheckIcon, CircleArrowOutUpRight, Clock, ClockFading, Infinity, Layers2, Parentheses, PlusIcon, Repeat2 } from "lucide-react"
import { Input, DefaultPopover, DefaultTooltip, CommandSearch, CommandItemSearch, Col, Row, InputGroup, InputGroupText, Select, Button, CommandGroupSearch } from "twyne-new.ui"

import { CubicBezierPopover, CubicBezierToggle, useCubicBezier } from "../components/CubicBezier"

import { useAnimation } from "./context"

const Configuration = ({ index = 0 }) => {
  const [displayedInputs, setDisplayedInputs] = useState()

  const { animationState } = useAnimation()
  const { direction, fill } = animationState[index]

  useEffect(() => {
    if (displayedInputs === undefined) {
      const newDisplayedInputs = ['duration', 'delay', 'ease', 'loops']

      if (direction) newDisplayedInputs.push('direction')
      if (fill) newDisplayedInputs.push('fill')

      setDisplayedInputs(newDisplayedInputs)
    }
  }, [displayedInputs, direction, fill])

  const handleChangeDisplayedInputs = input => {
    setDisplayedInputs(prev => prev.includes(input) ? prev.filter(item => item !== input) : [...prev, input])
  }

  if (displayedInputs === undefined) {
    return null
  }

  return <>
    <div className='flex gap-2 items-center my-1'>
      Configuration
      <DefaultPopover trigger={<ButtonTrigger />} contentProps={{ side: 'top' }} >
        <CommandSearch placeholder='Type to search animation property...'>
          <CommandGroupSearch heading=''>
            {INPUTS.map(input => <Item
              key={input}
              value={input}
              active={displayedInputs.includes(input)}
              onChange={handleChangeDisplayedInputs} />,
            )}
          </CommandGroupSearch>
        </CommandSearch>
      </DefaultPopover>

    </div>
    <Inputs displayedInputs={displayedInputs} index={index} />
  </>
}

const ButtonTrigger = () => <DefaultTooltip content='Advanced properties' triggerProps={{ asChild: true }}>
  <Button variant='light' size='sm'>
    <PlusIcon size='14' />
  </Button>
</DefaultTooltip>

const Inputs = ({ displayedInputs, index }) => {
  const { animationState, setAnimationState } = useAnimation()
  const { duration, delay, ease, loops, direction, fill } = animationState[index]

  const handleChange = (value, key) => {
    const newAnimation = cloneDeep(animationState)
    newAnimation[index][key] = value
    setAnimationState(newAnimation)
  }

  return <>
    <Row className='gap-1'>
      <Col size='6' className={displayedInputs.includes('duration') ? 'block' : 'hidden'}>
        <InputGroup>
          <DefaultTooltip content='Duration (in ms)'>
            <InputGroupText addonType='prepend'>
              <Clock size={14} />
            </InputGroupText>
          </DefaultTooltip>
          <Input
            placeholder='Duration'
            type='number'
            value={duration}
            onChange={e => handleChange(e.target.value, 'duration')}
          />
        </InputGroup>
      </Col>

      <Col size='6' className={displayedInputs.includes('delay') ? 'block' : 'hidden'}>
        <InputGroup>
          <DefaultTooltip content='Delay (in ms)'>
            <InputGroupText addonType='prepend'>
              <ClockFading size={14} />
            </InputGroupText>
          </DefaultTooltip>
          <Input
            placeholder='Delay'
            type='number'
            value={delay}
            onChange={e => handleChange(e.target.value, 'delay')}
          />
        </InputGroup>
      </Col>

      <TimingFunction displayedInputs={displayedInputs} onChange={handleChange} ease={ease} duration={duration} />

      <Col size='6' className={displayedInputs.includes('loops') ? 'flex gap-1' : 'hidden'}>
        <InputGroup>
          <DefaultTooltip content='Amount of loops'>
            <InputGroupText addonType='prepend'>
              <Repeat2 size={14} />
            </InputGroupText>
          </DefaultTooltip>
          <Input
            placeholder='Loops'
            type='number'
            value={loops}
            onChange={e => handleChange(e.target.value, 'loops')}
            disabled={loops === 'infinite'}
          />
        </InputGroup>

        <DefaultTooltip content='Infinite' triggerProps={{ asChild: true }}>
          <Button variant={loops === 'infinite' ? 'default' : 'light'} color='accent' size='sm' onClick={() => handleChange(loops === 'infinite' ? '' : 'infinite', 'loops')}>
            <Infinity size='14' />
          </Button>
        </DefaultTooltip>
      </Col>

      <Col size='6' className={displayedInputs.includes('direction') ? 'block' : 'hidden'}>
        <InputGroup>
          <DefaultTooltip content='Direction'>
            <InputGroupText addonType='prepend'>
              <CircleArrowOutUpRight size={14} />
            </InputGroupText>
          </DefaultTooltip>
          <Select
            value={direction}
            onChange={option => handleChange(option?.value, 'direction')}
            options={DIRECTION_OPTIONS}
            clearable
          />
        </InputGroup>
      </Col>

      <Col size='6' className={displayedInputs.includes('fill') ? 'block' : 'hidden'}>
        <InputGroup>
          <DefaultTooltip content='Fill'>
            <InputGroupText addonType='prepend'>
              <Layers2 size={14} />
            </InputGroupText>
          </DefaultTooltip>
          <Select
            value={fill}
            onChange={option => handleChange(option?.value, 'fill')}
            options={FILL_OPTIONS}
            clearable
          />
        </InputGroup>
      </Col>
    </Row>
  </>
}

const TimingFunction = ({ displayedInputs, onChange, ease, duration }) => {
  const { cubic, onChangeCubic } = useCubicBezier({ value: ease, easeOptions: EASE_OPTIONS })

  const handleChangeCubicToggle = value => {
    if (value === 'cubic') {
      onChange([{ x: 0, y: 0 }, { x: 0.42, y: 0 }, { x: 1, y: 1 }, { x: 1, y: 1 }], 'ease')
    } else {
      onChange(undefined, 'ease')
    }

    onChangeCubic(value === 'cubic')
  }

  return <>
    <Col size='6' className={displayedInputs.includes('ease') ? 'block' : 'hidden'}>
      <div className='flex items-center gap-1'>
        <InputGroup className='w-full'>
          <DefaultTooltip content='Timing Function'>
            <InputGroupText addonType='prepend'>
              <Parentheses size={14} />
            </InputGroupText>
          </DefaultTooltip>
          {cubic ? <>
            <CubicBezierPopover
              duration={duration}
              value={ease}
              onChange={ease => onChange(ease, 'ease')}
            />
          </> : <>
            <Select
              value={ease}
              onChange={option => onChange(option?.value, 'ease')}
              options={EASE_OPTIONS}
              clearable
            />
          </>}
        </InputGroup>
        <CubicBezierToggle value={cubic} onChange={handleChangeCubicToggle} />
      </div>
    </Col>
  </>
}
const Item = ({ value, active, onChange }) => <>
  <div onClick={() => onChange(value)} className="group">
    <CommandItemSearch key={value?.name || value} className="twy-text-base hover:twy-bg-base-neutral cursor-pointer flex items-center justify-between">
      <div className="flex items-center">
        {value?.name || value}
        {active && <CheckIcon size='12' className='ml-1' />}
      </div>
    </CommandItemSearch>
  </div>
</>

const DIRECTION_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'reverse', label: 'Reverse' },
  { value: 'alternate', label: 'Alternate' },
  { value: 'alternate-reverse', label: 'Alternate Reverse' },
]

const FILL_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'forwards', label: 'Forwards' },
  { value: 'backwards', label: 'Backwards' },
  { value: 'both', label: 'Both' },
]

export const EASE_OPTIONS = [
  { value: 'ease', label: 'Ease' },
  { value: 'linear', label: 'Linear' },
  { value: 'ease-in-out', label: 'In out' },
  { value: 'ease-in', label: 'In' },
  { value: 'ease-out', label: 'Out' },
]

const INPUTS = ['duration', 'delay', 'ease', 'loops', 'direction', 'fill']

export default Configuration
